import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Button, Empty, Tag, Typography } from 'antd';
import { AimOutlined, ApartmentOutlined, DownOutlined, EnvironmentOutlined, RightOutlined } from '@ant-design/icons';
import Map, { Layer, Source, Marker, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../../style/Areas.css';
import '../../style/AreaMap.css';

const { Text } = Typography;

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

// Hierarchy-based color palette (same as AreaMap)
const HIERARCHY_COLORS = [
    { fill: '#2f54eb', fillOpacity: 0.2, line: '#1d39c4' },  // Level 0: Blue
    { fill: '#52c41a', fillOpacity: 0.2, line: '#389e0d' },  // Level 1: Green
    { fill: '#fa8c16', fillOpacity: 0.2, line: '#d46b08' },  // Level 2: Orange
    { fill: '#eb2f96', fillOpacity: 0.2, line: '#c41d7f' },  // Level 3: Pink
    { fill: '#722ed1', fillOpacity: 0.2, line: '#531dab' },  // Level 4: Purple
    { fill: '#13c2c2', fillOpacity: 0.2, line: '#08979c' },  // Level 5: Cyan
];

const getColorForDepth = (depth: number = 0) => {
    return HIERARCHY_COLORS[depth % HIERARCHY_COLORS.length];
};

const isCoordinatePair = (coord: any): coord is [number, number] => (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    Number.isFinite(Number(coord[0])) &&
    Number.isFinite(Number(coord[1]))
);

const normalizeRing = (ring: any): number[][] => {
    if (!Array.isArray(ring)) return [];
    return ring
        .filter(isCoordinatePair)
        .map(([lng, lat]) => [Number(lng), Number(lat)]);
};

const getPrimaryAreaRing = (geom: any): number[][] => {
    if (!geom?.coordinates) return [];

    if (geom.type === 'Polygon') {
        return normalizeRing(geom.coordinates?.[0]);
    }

    if (geom.type === 'MultiPolygon') {
        const polygons = Array.isArray(geom.coordinates) ? geom.coordinates : [];
        for (const polygon of polygons) {
            const ring = normalizeRing(polygon?.[0]);
            if (ring.length > 0) return ring;
        }
    }

    return [];
};

const getAreaPolygonCoordinates = (geom: any): number[][] => {
    if (!geom?.coordinates) return [];

    if (geom.type === 'Polygon') {
        return getPrimaryAreaRing(geom);
    }

    if (geom.type === 'MultiPolygon') {
        return (Array.isArray(geom.coordinates) ? geom.coordinates : [])
            .flatMap((polygon: any) => normalizeRing(polygon?.[0]));
    }

    return [];
};

const hasRenderableAreaGeometry = (geom: any) => (
    (geom?.type === 'Polygon' || geom?.type === 'MultiPolygon') &&
    getPrimaryAreaRing(geom).length >= 3
);

interface AreaType {
    id: number;
    name: string;
    code?: string;
}

interface Area {
    id: number;
    name: string;
    code?: string;
    areaType?: AreaType | null;
    companyId: number;
    geom?: any;
    createdAt?: string;
    parentAreaId?: number | null;
    children?: Area[];
}

interface ReportAreaHierarchyProps {
    areas: Area[];
    areaTypeTemplates?: Map<number, string>;
    title?: string;
    showMap?: boolean;
    selectedParentAreaId?: number | null;
    selectedAreaId?: number | null;
    measurements?: Array<{
        id: number;
        measurementCode?: string;
        areaId?: number | null;
        geom?: any;
        patternPoint?: { id?: number; label?: string; pointIndex?: number } | null;
    }>;
    onAreaClick?: (areaId: number) => void;
    onMeasurementClick?: (measurementId: number, areaId?: number | null) => void;
}

export const ReportAreaHierarchy: React.FC<ReportAreaHierarchyProps> = ({
    areas = [],
    areaTypeTemplates,
    title = 'Area Hierarchy',
    showMap = true,
    selectedParentAreaId = null,
    selectedAreaId = null,
    measurements = [],
    onAreaClick,
    onMeasurementClick,
}) => {
    const mapRef = useRef<MapRef>(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    
    // Build tree structure from flat areas array
    const treeData = useMemo(() => {
        if (!areas || areas.length === 0) return [];
        
        const map: { [key: number]: Area } = {};
        const roots: Area[] = [];
        
        // Create area map
        areas.forEach(item => {
            map[item.id] = { ...item, children: [] };
        });
        
        // Build tree
        areas.forEach(item => {
            if (item.parentAreaId && map[item.parentAreaId]) {
                map[item.parentAreaId].children!.push(map[item.id]);
            } else {
                roots.push(map[item.id]);
            }
        });
        
        return roots;
    }, [areas]);

    // Get the root area (first one) for map display
    const rootArea = useMemo(() => {
        return treeData.length > 0 ? treeData[0] : null;
    }, [treeData]);

    // Collect all areas with hierarchy depth for map display
    const areasForMap = useMemo(() => {
        const collectChildren = (area: Area, depth: number = 0): Array<Area & { hierarchyDepth: number }> => {
            const result = [{ ...area, hierarchyDepth: depth }];
            if (area.children && area.children.length > 0) {
                area.children.forEach(child => {
                    result.push(...collectChildren(child, depth + 1));
                });
            }
            return result;
        };
        
        if (!treeData.length) return [];
        return treeData.flatMap((area) => collectChildren(area));
    }, [treeData]);

    const selectedAreaNode = useMemo(() => {
        if (!selectedAreaId) return null;
        return areasForMap.find((area) => area.id === selectedAreaId) || null;
    }, [areasForMap, selectedAreaId]);

    const measurementMarkers = useMemo(() => {
        return (measurements || [])
            .filter((m) => m?.areaId === selectedAreaId)
            .filter((m) => m?.geom?.type === 'Point' && Array.isArray(m.geom.coordinates))
            .filter((m) => isCoordinatePair(m.geom.coordinates))
            .map((m) => ({
                id: m.id,
                areaId: m.areaId ?? null,
                lng: Number(m.geom.coordinates[0]),
                lat: Number(m.geom.coordinates[1]),
                label: m.patternPoint?.label || m.measurementCode || `Measurement ${m.id}`,
                measurementCode: m.measurementCode,
            }));
        }, [measurements, selectedAreaId]);

    const totalAreaCount = areasForMap.length;

    // Calculate map center from all areas
    const mapCenter = useMemo(() => {
        if (!areasForMap || areasForMap.length === 0) {
            return { longitude: -0.1278, latitude: 51.5074, zoom: 13, bearing: 0, pitch: 0 };
        }
        
        // Collect all coordinates
        const allCoords: number[][] = [];
        areasForMap.forEach(area => {
            allCoords.push(...getAreaPolygonCoordinates(area.geom));
        });
        
        if (allCoords.length === 0) {
            return { longitude: -0.1278, latitude: 51.5074, zoom: 13, bearing: 0, pitch: 0 };
        }
        
        const lons = allCoords.map(c => c[0]);
        const lats = allCoords.map(c => c[1]);
        const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        
        return { longitude: centerLon, latitude: centerLat, zoom: 14, bearing: 0, pitch: 0 };
    }, [areasForMap]);

    // Expand all rows when root area changes
    useEffect(() => {
        if (!areasForMap || areasForMap.length === 0) return;
        
        // Collect all area IDs to expand all rows
        const allKeys = areasForMap.map(area => area.id);
        setExpandedRowKeys(allKeys);
    }, [treeData]);
    
    // Animate map to focus on selected area
    useEffect(() => {
        if (!mapRef.current || !areasForMap || areasForMap.length === 0 || !selectedAreaId) return;
        
        // Find the selected area's geometry
        const selectedAreaObj = areasForMap.find(a => a.id === selectedAreaId);
        
        // Collect coordinates from selected area for zoom
        const coordsToFit: number[][] = [];
        coordsToFit.push(...getAreaPolygonCoordinates(selectedAreaObj?.geom));
        
        if (coordsToFit.length === 0) return;
        
        // Delay to ensure map is loaded before animation
        setTimeout(() => {
            if (mapRef.current && coordsToFit.length > 0) {
                const lons = coordsToFit.map(c => c[0]);
                const lats = coordsToFit.map(c => c[1]);
                const bounds: [[number, number], [number, number]] = [
                    [Math.min(...lons), Math.min(...lats)],
                    [Math.max(...lons), Math.max(...lats)]
                ];
                
                mapRef.current.fitBounds(bounds, {
                    padding: { top: 80, bottom: 80, left: 80, right: 80 },
                    duration: 1200
                });
            }
        }, 100);
    }, [selectedAreaId, areasForMap]); // Re-run when selected area changes

    useEffect(() => {
        if (!tableWrapperRef.current || !selectedAreaId) return;
        const tableBody = tableWrapperRef.current;
        const target = tableWrapperRef.current.querySelector(
            `.report-area-tree-node-${selectedAreaId}`
        ) as HTMLElement | null;
        if (!tableBody || !target) return;

        const rowTop = target.offsetTop;
        const rowBottom = rowTop + target.offsetHeight;
        const viewTop = tableBody.scrollTop;
        const viewBottom = viewTop + tableBody.clientHeight;

        if (rowTop < viewTop || rowBottom > viewBottom) {
            const centeredTop = rowTop - (tableBody.clientHeight - target.offsetHeight) / 2;
            tableBody.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
        }
    }, [selectedAreaId, treeData, expandedRowKeys]);

    const toggleAreaExpanded = (area: Area) => {
        const collectSubtreeIds = (node: Area): number[] => {
            const childIds = node.children?.flatMap((child) => collectSubtreeIds(child)) || [];
            return [node.id, ...childIds];
        };

        const subtreeIds = new Set(collectSubtreeIds(area));
        const isExpanded = expandedRowKeys.includes(area.id);

        if (isExpanded) {
            setExpandedRowKeys((prev) => prev.filter((key) => !subtreeIds.has(Number(key))));
            return;
        }

        setExpandedRowKeys((prev) => Array.from(new Set([...prev, area.id])));
    };

    const renderTreeNodes = (nodes: Area[], depth = 0): React.ReactNode => {
        return nodes.map((area) => {
            const hasChildren = Boolean(area.children && area.children.length > 0);
            const isExpanded = expandedRowKeys.includes(area.id);
            const isSelected = area.id === selectedAreaId;
            const canNavigate = Boolean(onAreaClick) && !isSelected;
            const childCount = area.children?.length || 0;

            return (
                <div key={area.id} className={`report-area-tree-node report-area-tree-node-${area.id}`}>
                    <div
                        className={`report-area-tree-row ${isSelected ? 'report-area-tree-row-selected' : ''}`}
                        style={{ paddingLeft: 14 + depth * 18 }}
                    >
                        {hasChildren ? (
                            <Button
                                type="text"
                                size="small"
                                className="report-area-tree-switcher"
                                icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleAreaExpanded(area);
                                }}
                            />
                        ) : (
                            <span className="report-area-tree-switcher-spacer" />
                        )}

                        <div
                            className={`report-area-tree-main ${canNavigate ? 'report-area-tree-main-clickable' : ''}`}
                            onClick={() => {
                                if (canNavigate && onAreaClick) onAreaClick(area.id);
                            }}
                            role={canNavigate ? 'button' : undefined}
                            tabIndex={canNavigate ? 0 : -1}
                            onKeyDown={(event) => {
                                if (!canNavigate || !onAreaClick) return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onAreaClick(area.id);
                                }
                            }}
                        >
                            <div className="report-area-tree-text">
                                <div className="report-area-tree-name-line">
                                    <span className="report-area-tree-name">{area.name}</span>
                                    {childCount > 0 ? <span className="report-area-tree-count">{childCount}</span> : null}
                                </div>
                                <div className="report-area-tree-meta">
                                    <Tag color={isSelected ? 'cyan' : 'blue'}>{area.areaType?.name || 'No type'}</Tag>
                                    {area.code ? <Text type="secondary">{area.code}</Text> : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    {hasChildren && isExpanded ? (
                        <div className="report-area-tree-children">{renderTreeNodes(area.children!, depth + 1)}</div>
                    ) : null}
                </div>
            );
        });
    };

    if (!areas || areas.length === 0) {
        return <Empty description="No areas available" />;
    }

    return (
        <div className="report-area-hierarchy">
            <div className="report-area-hierarchy-head">
                <div className="report-area-hierarchy-title">{title}</div>
                <div className="report-area-hierarchy-meta">
                    {selectedAreaNode?.name || rootArea?.name || `${totalAreaCount} areas`}
                </div>
            </div>

            <div className={`report-area-hierarchy-body ${showMap ? '' : 'report-area-hierarchy-body--single'}`}>
                <aside className="report-area-hierarchy-rail">
                    <div className="report-area-hierarchy-section-head">
                        <div>
                            <div className="report-area-hierarchy-section-title">
                                <ApartmentOutlined /> Area hierarchy
                            </div>
                            <Text type="secondary" className="report-area-hierarchy-section-subtitle">
                                {selectedAreaNode
                                    ? `${totalAreaCount} areas • Focused on ${selectedAreaNode.name}`
                                    : `${totalAreaCount} areas in this hierarchy`}
                            </Text>
                        </div>
                    </div>

                    <div ref={tableWrapperRef} className="report-area-tree-scroll">
                        {treeData.length === 0 ? <Empty description="No areas found" /> : renderTreeNodes(treeData)}
                    </div>
                </aside>

                {showMap && (
                    <div className="report-area-hierarchy-main">
                        <div className="report-area-hierarchy-section-head">
                            <div>
                                <div className="report-area-hierarchy-section-title">
                                    <EnvironmentOutlined /> Map
                                </div>
                                <Text type="secondary" className="report-area-hierarchy-section-subtitle">
                                    {selectedAreaNode?.name || rootArea?.name || title}
                                </Text>
                            </div>
                        </div>

                        <div className="areas-map-wrapper report-area-hierarchy-map-canvas" style={{ height: 420, minHeight: 420 }}>
                            <Map
                                ref={mapRef}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                                initialViewState={mapCenter}
                                style={{ width: '100%', height: '100%' }}
                                onLoad={(event) => {
                                    const map = event.target;

                                    const selectedAreaObj = areasForMap.find(a => a.id === selectedAreaId);
                                    const coordsToFit: number[][] = [];

                                    coordsToFit.push(...getAreaPolygonCoordinates(selectedAreaObj?.geom));

                                    if (coordsToFit.length > 0) {
                                        const lons = coordsToFit.map(c => c[0]);
                                        const lats = coordsToFit.map(c => c[1]);
                                        const bounds: [[number, number], [number, number]] = [
                                            [Math.min(...lons), Math.min(...lats)],
                                            [Math.max(...lons), Math.max(...lats)]
                                        ];

                                        map.fitBounds(bounds, {
                                            padding: { top: 80, bottom: 80, left: 80, right: 80 },
                                            duration: 800
                                        });
                                    }
                                }}
                            >
                                {areaTypeTemplates && areasForMap.map(area => {
                                    const templateUrl = area.areaType?.id ? areaTypeTemplates.get(area.areaType.id) : null;
                                    if (!templateUrl || !area.geom) return null;

                                    const areaCoords = getPrimaryAreaRing(area.geom);

                                    const trimmed = areaCoords.length > 1 &&
                                        areaCoords[0][0] === areaCoords[areaCoords.length - 1][0] &&
                                        areaCoords[0][1] === areaCoords[areaCoords.length - 1][1]
                                        ? areaCoords.slice(0, -1)
                                        : areaCoords;

                                    if (trimmed.length < 4) return null;

                                    const vertices = trimmed.slice(0, 4);
                                    const bounds: [[number, number], [number, number], [number, number], [number, number]] = [
                                        [vertices[3][0], vertices[3][1]],
                                        [vertices[2][0], vertices[2][1]],
                                        [vertices[1][0], vertices[1][1]],
                                        [vertices[0][0], vertices[0][1]]
                                    ];

                                    return (
                                        <Source
                                            key={`template-${area.id}`}
                                            id={`template-${area.id}`}
                                            type="image"
                                            url={templateUrl}
                                            coordinates={bounds}
                                        >
                                            <Layer
                                                id={`template-layer-${area.id}`}
                                                type="raster"
                                                paint={{ 'raster-opacity': 0.5 }}
                                            />
                                        </Source>
                                    );
                                })}

                                {areasForMap.map((area) => {
                                    if (!hasRenderableAreaGeometry(area.geom)) return null;
                                    const isSelected = area.id === selectedAreaId;
                                    const colors = isSelected
                                        ? { fill: '#faad14', fillOpacity: 0.4, line: '#d48806' }
                                        : getColorForDepth(area.hierarchyDepth || 0);

                                    return (
                                        <Source
                                            key={`area-${area.id}`}
                                            id={`area-${area.id}`}
                                            type="geojson"
                                            data={{
                                                type: 'Feature',
                                                geometry: area.geom,
                                                properties: { id: area.id, name: area.name || '' }
                                            }}
                                        >
                                            <Layer
                                                id={`area-fill-${area.id}`}
                                                type="fill"
                                                paint={{
                                                    'fill-color': colors.fill,
                                                    'fill-opacity': colors.fillOpacity
                                                }}
                                            />
                                            <Layer
                                                id={`area-outline-${area.id}`}
                                                type="line"
                                                paint={{
                                                    'line-color': colors.line,
                                                    'line-width': isSelected ? 3 : 2
                                                }}
                                            />
                                        </Source>
                                    );
                                })}

                                {measurementMarkers.map((m) => (
                                    <Marker key={`measurement-${m.id}`} longitude={m.lng} latitude={m.lat} anchor="center">
                                        <div
                                            className="measurement-marker"
                                            title={m.label}
                                            role={onMeasurementClick ? 'button' : undefined}
                                            tabIndex={onMeasurementClick ? 0 : -1}
                                            style={{ cursor: onMeasurementClick ? 'pointer' : 'default' }}
                                            onClick={(event) => {
                                                if (!onMeasurementClick) return;
                                                event.preventDefault();
                                                event.stopPropagation();
                                                onMeasurementClick(m.id, m.areaId ?? selectedAreaId ?? null);
                                            }}
                                            onKeyDown={(event) => {
                                                if (!onMeasurementClick) return;
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    onMeasurementClick(m.id, m.areaId ?? selectedAreaId ?? null);
                                                }
                                            }}
                                        />
                                    </Marker>
                                ))}
                            </Map>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportAreaHierarchy;
